import React, { useCallback, useEffect, useMemo, useState } from 'react'

import env from '@/networkRequests/apiClient/env.config'
import routes from '@/router/routes.dictionary'
import { ButtonProps } from '@/shared/components/Button/Button.types'
import localStorage from '@/shared/functions/LocalStorage/localStorage'
import { Box } from '@mui/material'

import PaginatedPage from '../../shared/components/PaginatedPage/PaginatedPage.component'
import OfferItem from './components/OfferItem/OfferItem.component'

import { getAllFlightItineraries } from '../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.slice'
import {
  FlightItinerary,
  GetAllFlightItinerariesRequest
} from '../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { getPublicOffers } from '../../redux/states/offerRequest/getPublicOffers/getPublicOffers.slice'
import {
  GetPublicOffersRequest,
  OfferRequest
} from '../../redux/states/offerRequest/getPublicOffers/getPublicOffers.types'
import { RootState } from '../../redux/store/store.types'
import router from '../../router/functions/router.functions'
import { EmptyStateProps } from '../../shared/components/EmptyState/EmptyState.types'
import {
  PaginatedRenderProps,
  PaginatedResponse,
  SearchBarProps
} from '../../shared/components/PaginatedPage/PaginatedPage.types'
import {
  FilterSectionConfig,
  Filters
} from '../../shared/components/UniversalFilterSideBar/UniversalFilterSideBar.types'
import { isAuthenticated } from '../../shared/functions/Auth/auth.functions'
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux.hooks'
import { SHARED_OFFER_DETAILS_KEY } from './Offers.dictionary'
import { convertToPounds } from './Offers.functions'
import styles from './Offers.styles'
import { SharedOfferDetails } from './components/OfferItem/OfferItem.types'

const Offers: React.FC = () => {
  const { loading } = useAppSelector((state: RootState) => state.getPublicOffers)
  const [flightItineraries, setFlightItineraries] = useState<FlightItinerary[]>([])

  const dispatch = useAppDispatch()

  // Get values once - either from localStorage or URL
  const urlOfferRequestId = router.getUrlParam('offerRequestId')
  const urlShopperId = router.getUrlParam('shopperId')
  const initialSharedOfferDetails = localStorage.getItem(SHARED_OFFER_DETAILS_KEY) as SharedOfferDetails | null

  const [offerRequestId] = useState<string | null>(
    initialSharedOfferDetails ? initialSharedOfferDetails.offerRequestId : urlOfferRequestId
  )

  const [shopperId] = useState<string | null>(
    initialSharedOfferDetails ? initialSharedOfferDetails.shopperId : urlShopperId
  )

  useEffect(() => {
    if (initialSharedOfferDetails) {
      const timeoutId = setTimeout(() => {
        localStorage.removeItem(SHARED_OFFER_DETAILS_KEY)
      }, 100)

      return () => clearTimeout(timeoutId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchOffers = useCallback(
    async (page: number): Promise<PaginatedResponse<OfferRequest>> => {
      return new Promise((resolve) => {
        const getPublicOffersRequest: GetPublicOffersRequest = {
          page,
          size: env.DEFAULT_PAGE_SIZE,
          searchQuery: '',
          offerRequestId: offerRequestId || undefined,
          shopperId: shopperId || undefined
        }

        dispatch(
          getPublicOffers({
            request: getPublicOffersRequest,
            onSuccess: (response) => {
              resolve({
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages,
                results: response.data.results
              })
            }
          })
        )
      })
    },
    [dispatch, offerRequestId, shopperId]
  )

  useEffect(() => {
    const fetchFlightItineraries = async () => {
      if (!isAuthenticated()) return

      const getAllFlightItinerariesPayload: GetAllFlightItinerariesRequest = {}

      const response = await dispatch(
        getAllFlightItineraries({
          request: getAllFlightItinerariesPayload
        })
      ).unwrap()

      setFlightItineraries(response.data.results)
    }

    fetchFlightItineraries()
  }, [dispatch])

  const OfferItemsView = function (props: PaginatedRenderProps) {
    const offer = props.item as OfferRequest
    const sharedOfferDetails = {
      offerRequestId: offerRequestId || '',
      shopperId: shopperId || ''
    }

    return (
      <Box sx={styles.offerItemContainer} aria-label="offer-item-container">
        <OfferItem offer={offer} flightItineraries={flightItineraries} sharedOfferDetails={sharedOfferDetails} />
      </Box>
    )
  }

  const searchBar: SearchBarProps = {
    placeholder: 'Type any item name',
    onSearchFilter: (items, query) => {
      const lowerQuery = query.toLowerCase()

      return (items as OfferRequest[]).filter((item) =>
        item.offerRequestDetails.some(
          (detail) =>
            detail.itemDetails.title.toLowerCase().includes(lowerQuery) ||
            detail.itemDetails.country.name.toLowerCase().includes(lowerQuery) ||
            detail.itemDetails.country.name.toLowerCase().includes(lowerQuery)
        )
      )
    }
  }

  const getEmptyState = () => {
    let emptyState: EmptyStateProps | undefined

    if (offerRequestId) {
      emptyState = {
        title: 'This offer is no longer available',
        subtitle: 'See all available offers.',
        button: {
          text: 'View All Offers',
          onClick: () => {
            window.location.href = routes.offers.path
          }
        }
      }
    } else {
      emptyState = {
        title: 'No offers available',
        subtitle: 'Currently, there are no offers available. Please check back later.'
      }
    }

    return emptyState
  }

  const filterSections: (items: unknown[], filters: Filters) => FilterSectionConfig[] = useCallback(
    (items: unknown[]) => {
      const offers = items as OfferRequest[]

      // Extract unique source and delivery country pairs
      const destinations = offers.flatMap((offer) =>
        offer.offerRequestDetails.map((detail) => {
          const sourceCountry = detail.itemDetails.country.name
          const deliveryCountry = offer.shopperDetails.deliveryTo.country

          return `${sourceCountry} → ${deliveryCountry}`
        })
      )
      const uniqueDestinations = Array.from(new Set(destinations))

      const offerWeights = offers.map((offer) =>
        offer.offerRequestDetails.reduce(
          (total, item) => total + convertToPounds(item.itemDetails.weight, item.itemDetails.weightUnit),
          0
        )
      )
      let minWeight = Math.ceil(Math.min(...offerWeights))
      const maxWeight = Math.ceil(Math.max(...offerWeights))

      if (minWeight === 0) {
        minWeight = 1
      }

      const offerTravelerBenefits = offers.map((offer) => offer.travelerBenefit)
      const minTravelerBenefit = Math.floor(Math.min(...offerTravelerBenefits))
      const maxTravelerBenefit = Math.ceil(Math.max(...offerTravelerBenefits))

      const sections: FilterSectionConfig[] = [
        {
          id: 'destination',
          title: 'Destination',
          type: 'multi-select',
          options: [
            { primaryValue: 'All' },
            ...uniqueDestinations.map((destination) => ({ primaryValue: destination }))
          ],
          defaultValue: ['All'],
          filterDef: (item, filters) => {
            if (filters.destination && Array.isArray(filters.destination) && !filters.destination.includes('All')) {
              const sourceCountry = (item as OfferRequest).offerRequestDetails[0].itemDetails.country.name
              const deliveryCountry = (item as OfferRequest).shopperDetails.deliveryTo.country
              const itemDestination = `${sourceCountry} → ${deliveryCountry}`

              return filters.destination.includes(itemDestination)
            }

            return true
          }
        },
        {
          id: 'travelerBenefitRange',
          title: 'Flight Discount',
          type: 'slider',
          options: [{ primaryValue: minTravelerBenefit }, { primaryValue: maxTravelerBenefit }],
          unit: { symbol: '$', label: 'USD' },
          defaultValue: [minTravelerBenefit, maxTravelerBenefit],
          filterDef: (item, filters) => {
            const [minTravelerBenefit, maxTravelerBenefit] = filters.travelerBenefitRange as number[]
            const offerTravelerBenefit = (item as OfferRequest).travelerBenefit

            return offerTravelerBenefit >= minTravelerBenefit && offerTravelerBenefit <= maxTravelerBenefit
          }
        },
        {
          id: 'timePeriod',
          title: 'Time Period',
          type: 'multi-select',
          options: [
            { primaryValue: 'All' },
            { primaryValue: '1 Week' },
            { primaryValue: '2 Weeks' },
            { primaryValue: '3 Weeks' },
            { primaryValue: 'Anytime' }
          ],
          defaultValue: ['All'],
          filterDef: (item, filters) => {
            if (filters.timePeriod && Array.isArray(filters.timePeriod) && !filters.timePeriod.includes('All')) {
              const offerNeededByDate = (item as OfferRequest).neededByDate

              if (filters.timePeriod.includes('Anytime') && !offerNeededByDate) {
                //Anytime
                return true
              }

              const neededByDate = new Date(offerNeededByDate)

              const currentDate = new Date()
              const weeksDifference = Math.ceil(
                (neededByDate.getTime() - currentDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
              )
              const selectedWeeks = filters.timePeriod.map((period: string) => parseInt(period.split(' ')[0]))

              return selectedWeeks.includes(weeksDifference)
            }

            return true
          }
        }
      ]

      if (minWeight !== maxWeight) {
        sections.splice(1, 0, {
          id: 'weight',
          title: 'Weight',
          type: 'slider',
          options: [{ primaryValue: minWeight }, { primaryValue: maxWeight }],
          unit: { symbol: '', label: 'lbs' },
          defaultValue: [minWeight, maxWeight],
          filterDef: (item, filters) => {
            const [minWeight, maxWeight] = filters.weight as number[]
            const offerWeight = (item as OfferRequest).offerRequestDetails.reduce((total, item) => {
              if (!item.itemDetails.weightUnit) {
                return total + 1
              }

              return total + convertToPounds(item.itemDetails.weight, item.itemDetails.weightUnit || 'lbs')
            }, 0)

            return offerWeight >= minWeight - 1 && offerWeight <= maxWeight
          }
        })
      }

      return sections
    },
    []
  )

  const headerButton = useMemo(() => {
    if (offerRequestId) {
      const button: ButtonProps = {
        text: 'View All Offers',
        buttonType: 'tertiary',
        onClick: () => {
          window.location.href = routes.offers.path
        }
      }

      return button
    }

    return undefined
  }, [offerRequestId])

  return (
    <Box sx={styles.container}>
      <PaginatedPage
        title={{
          text: offerRequestId ? 'Shared Offer' : 'Offers',
          id: offerRequestId ? 'Shared Offer' : 'Offers'
        }}
        subtitle={offerRequestId ? 'You are viewing a shared offer' : 'Browse available offers'}
        headerButton={headerButton}
        isFetchingData={loading}
        fetchCall={fetchOffers}
        renderListData={OfferItemsView}
        emptyState={getEmptyState()}
        layout={offerRequestId ? 'list' : 'masonry'}
        verticalFilters={offerRequestId ? undefined : filterSections}
        searchBar={offerRequestId ? undefined : searchBar}
        removeExtraPadding={offerRequestId ? true : false}
      />
    </Box>
  )
}

export default Offers
